import { Link } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import type { MobileTab } from './types';

const useStyles = makeStyles({
  // Each tab fills an equal share of the bar. 44px min tap target (mock 3a-3f).
  tab: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    paddingTop: 8,
    minHeight: 44,
    border: 'none',
    background: 'none',
    textDecoration: 'none',
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
  },
  // Active tab: a 22x3 yellow tick butting the top edge, over the icon.
  tick: {
    position: 'absolute',
    top: 0,
    width: 22,
    height: 3,
    borderRadius: '0 0 2px 2px',
    background: 'var(--helix-colors-brand-500)',
  },
  icon: {
    width: 21,
    height: 21,
    display: 'block',
  },
  iconResting: { opacity: 0.38 },
  label: {
    fontSize: 10.5,
    fontWeight: 500,
    color: 'var(--hone-portal-fg-tertiary)',
    lineHeight: 1,
  },
  labelActive: {
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
});

// One destination in the mobile bottom tab bar. Renders a router link for a
// route tab, or a button for an action tab (Search opens the search sheet).
export function MobileTabBarItem({ tab }: { tab: MobileTab }) {
  const classes = useStyles();
  const TabIcon = tab.icon;

  const body = (
    <>
      {tab.active && <span className={classes.tick} />}
      <TabIcon
        className={`${classes.icon} ${tab.active ? '' : classes.iconResting}`}
      />
      <span
        className={`${classes.label} ${tab.active ? classes.labelActive : ''}`}
      >
        {tab.label}
      </span>
    </>
  );

  if (tab.to) {
    return (
      <Link
        to={tab.to}
        underline="none"
        className={classes.tab}
        aria-current={tab.active ? 'page' : undefined}
      >
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={tab.onClick}
      className={classes.tab}
      aria-label={tab.label}
    >
      {body}
    </button>
  );
}
